/**
 * Pure 2D geometry used by bots. Mirrors the contract's shape semantics so bots can
 * reason about telegraphs even before (or independently of) the spatial system:
 *   circle/ring: centred on origin; cone: apex at origin along facing (total angle);
 *   line: starts at origin, `length` along facing, `width` across;
 *   rect: centred on origin, `h` along facing, `w` across.
 */
import type { DeepReadonly, Vec2, WorldShape } from '@keystone/sim';

export const vec = (x: number, y: number): Vec2 => ({ x, y });
export const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Vec2, k: number): Vec2 => ({ x: a.x * k, y: a.y * k });
export const len = (a: Vec2): number => Math.hypot(a.x, a.y);
export const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
export const fromAngle = (r: number): Vec2 => ({ x: Math.cos(r), y: Math.sin(r) });
export const angleOf = (a: Vec2): number => Math.atan2(a.y, a.x);
export const perp = (a: Vec2): Vec2 => ({ x: -a.y, y: a.x });

export function norm(a: Vec2, fallback: Vec2 = { x: 1, y: 0 }): Vec2 {
  const l = len(a);
  return l < 1e-6 ? { ...fallback } : { x: a.x / l, y: a.y / l };
}

/** Smallest signed difference a - b in (-PI, PI]. */
export function angleDiff(a: number, b: number): number {
  let d = (a - b) % (2 * Math.PI);
  if (d > Math.PI) d -= 2 * Math.PI;
  if (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

export function centroid(points: readonly Vec2[]): Vec2 | null {
  if (points.length === 0) return null;
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / points.length, y: y / points.length };
}

/** Is a circle (p, radius) touching the placed shape. */
export function shapeContains(area: DeepReadonly<WorldShape>, p: Vec2, radius = 0): boolean {
  const s = area.shape;
  const rel = sub(p, area.origin);
  const d = len(rel);
  const dir = fromAngle(area.facing);
  const fwd = rel.x * dir.x + rel.y * dir.y;
  const lat = Math.abs(rel.x * -dir.y + rel.y * dir.x);
  switch (s.type) {
    case 'circle':
      return d <= s.radius + radius;
    case 'ring':
      return d >= s.inner - radius && d <= s.outer + radius;
    case 'cone': {
      if (d > s.range + radius) return false;
      if (d <= radius) return true;
      const slack = Math.asin(Math.min(1, radius / d));
      return Math.abs(angleDiff(angleOf(rel), area.facing)) <= s.angle / 2 + slack;
    }
    case 'line':
      return fwd >= -radius && fwd <= s.length + radius && lat <= s.width / 2 + radius;
    case 'rect':
      return Math.abs(fwd) <= s.h / 2 + radius && lat <= s.w / 2 + radius;
  }
}

/**
 * Closest point to `from` (biased toward `prefer`) whose circle of `radius` is outside
 * every danger and passes `ok`. Deterministic sampling on rings of growing radius.
 */
export function findSafePoint(
  from: Vec2,
  dangers: readonly DeepReadonly<WorldShape>[],
  radius: number,
  ok: (p: Vec2) => boolean,
  prefer?: Vec2,
  maxDist = 40,
): Vec2 | null {
  const margin = radius + 0.75;
  const safe = (p: Vec2): boolean => dangers.every((d) => !shapeContains(d, p, margin)) && ok(p);
  if (safe(from)) return { ...from };
  const ANGLES = 24;
  for (let r = 1.5; r <= maxDist; r += 1.5) {
    let best: Vec2 | null = null;
    let bestScore = Infinity;
    for (let i = 0; i < ANGLES; i++) {
      const c = add(from, scale(fromAngle((i / ANGLES) * 2 * Math.PI), r));
      if (!safe(c)) continue;
      const score = prefer ? dist(c, prefer) : i;
      if (score < bestScore) {
        bestScore = score;
        best = c;
      }
    }
    if (best) return best;
  }
  return null;
}
